import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { formatFechaHora } from "../../lib/dates";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { RedsysRecoveryPanel } from "../../components/pago/RedsysRecoveryPanel";

interface ConfigResponse {
  configurado: boolean;
  merchantCode?: string;
  terminal?: string;
  environment?: string;
  updatedAt?: string;
}

interface ApiKeyRow {
  id: number;
  nombre: string;
  key_preview: string;
  activa: boolean;
  created_at: string;
  last_used_at: string | null;
}

export function ConfiguracionPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState<ConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyNombre, setApiKeyNombre] = useState("");
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [newApiKeySecret, setNewApiKeySecret] = useState<string | null>(null);
  const [newApiKeyPreview, setNewApiKeyPreview] = useState<string | null>(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [apiKeyAcknowledged, setApiKeyAcknowledged] = useState(false);
  const [revocarApiKeyId, setRevocarApiKeyId] = useState<number | null>(null);

  const [merchantCode, setMerchantCode] = useState("");
  const [terminal, setTerminal] = useState("001");
  const [merchantKey, setMerchantKey] = useState("");
  const [environment, setEnvironment] = useState<"test" | "live">("test");

  const fetchApiKeys = useCallback(async () => {
    try {
      const res = await api.get<ApiKeyRow[]>("/cliente/api-keys");
      setApiKeys(Array.isArray(res.data) ? res.data : []);
    } catch {
      setApiKeys([]);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const redsysRes = await api.get<ConfigResponse>("/cliente/configuracion");
      setStatus(redsysRes.data);
      setShowForm(!redsysRes.data.configurado);
      await fetchApiKeys();
    } catch {
      setError("No se pudo cargar la configuración.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [fetchApiKeys]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await api.put<ConfigResponse>("/cliente/configuracion", {
        merchantCode: merchantCode.trim(),
        terminal: terminal.trim(),
        merchantKey: merchantKey.trim(),
        environment,
      });
      setSuccessMsg(
        "✓ Configuración guardada. Los enlaces QR de tu circuito ya aceptarán pago con tarjeta."
      );
      setMerchantKey("");
      await fetchConfig();
      setShowForm(false);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "No se pudo guardar."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar la configuración de pasarela? Los QR dejarán de cobrar con tarjeta.")) return;
    setDeleting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await api.delete("/cliente/configuracion");
      setSuccessMsg(null);
      setStatus({ configurado: false });
      setShowForm(true);
      setMerchantCode("");
      setTerminal("001");
      setMerchantKey("");
      setEnvironment("test");
    } catch {
      setError("No se pudo eliminar la configuración.");
    } finally {
      setDeleting(false);
    }
  };

  const openCrearApiKey = () => {
    setApiKeyNombre("");
    setApiKeyModalOpen(true);
  };

  const handleCrearApiKey = async () => {
    const nombre = apiKeyNombre.trim();
    if (!nombre) return;
    setCreatingApiKey(true);
    setError(null);
    try {
      const res = await api.post<{ api_key: string; key_preview: string }>("/cliente/api-keys", {
        nombre,
      });
      setApiKeyModalOpen(false);
      setNewApiKeySecret(res.data.api_key);
      setNewApiKeyPreview(res.data.key_preview);
      setApiKeyCopied(false);
      setApiKeyAcknowledged(false);
      await fetchApiKeys();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "No se pudo crear la API key."
      );
    } finally {
      setCreatingApiKey(false);
    }
  };

  const copyApiKey = async () => {
    if (!newApiKeySecret) return;
    try {
      await navigator.clipboard.writeText(newApiKeySecret);
      setApiKeyCopied(true);
    } catch {
      /* ignore */
    }
  };

  const handleRevocarApiKey = async () => {
    if (revocarApiKeyId == null) return;
    setError(null);
    try {
      await api.delete(`/cliente/api-keys/${revocarApiKeyId}`);
      setRevocarApiKeyId(null);
      await fetchApiKeys();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "No se pudo revocar la API key."
      );
    }
  };

  if (loading) {
    return <p className="text-gray-600">Cargando…</p>;
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Configuración</h1>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {successMsg && <p className="text-sm text-green-700">{successMsg}</p>}

      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div>
          <h2 className="text-lg font-medium text-[#2C2C2C]">Integraciones API</h2>
          <p className="text-sm text-gray-600">
            Usa tu API key para integrar Circuit Cover con Zapier u otras herramientas de automatización.
          </p>
        </div>

        {apiKeys.length === 0 ? (
          <p className="text-sm text-gray-600">No tienes API keys. Genera una para empezar.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-2 pr-3 font-medium">Nombre</th>
                  <th className="pb-2 pr-3 font-medium">Preview</th>
                  <th className="pb-2 pr-3 font-medium">Último uso</th>
                  <th className="pb-2 pr-3 font-medium">Estado</th>
                  <th className="pb-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((k) => (
                  <tr key={k.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-3">{k.nombre}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{k.key_preview}</td>
                    <td className="py-2 pr-3 text-gray-600">
                      {k.last_used_at ? formatFechaHora(k.last_used_at) : "—"}
                    </td>
                    <td className="py-2 pr-3">{k.activa ? "Activa" : "Revocada"}</td>
                    <td className="py-2">
                      {k.activa && (
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          onClick={() => setRevocarApiKeyId(k.id)}
                        >
                          Revocar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Button type="button" onClick={openCrearApiKey}>
          Generar nueva API key
        </Button>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-medium text-[#2C2C2C]">Pasarela de pago QR</h2>
          <p className="text-sm text-gray-600">
            Credenciales Redsys para cobrar con tarjeta en los enlaces QR de contratación de tu circuito.
          </p>
        </div>

        {status?.configurado && !showForm && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <p className="font-medium text-green-700">✓ Pasarela de pago configurada</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Código de comercio</dt>
                <dd className="text-right font-mono">{status.merchantCode ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Terminal</dt>
                <dd>{status.terminal ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Entorno</dt>
                <dd>{status.environment === "live" ? "Producción" : "Test"}</dd>
              </div>
              {status.updatedAt && (
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Actualizado</dt>
                  <dd className="text-right text-xs text-gray-600">{status.updatedAt}</dd>
                </div>
              )}
            </dl>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => { setShowForm(true); setSuccessMsg(null); }}>
                Actualizar credenciales
              </Button>
              <Button type="button" variant="secondary" className="text-red-700" loading={deleting} onClick={() => void handleDelete()}>
                Eliminar configuración
              </Button>
            </div>
          </div>
        )}

        {(showForm || !status?.configurado) && (
          <form onSubmit={(e) => void handleSave(e)} className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <Input
              label="Código de comercio"
              value={merchantCode}
              onChange={(e) => setMerchantCode(e.target.value)}
              required
              autoComplete="off"
            />
            <Input
              label="Terminal"
              value={terminal}
              onChange={(e) => setTerminal(e.target.value)}
              required
              placeholder="001"
            />
            <Input
              label="Clave de firma (clave de cifrado Redsys)"
              type="password"
              value={merchantKey}
              onChange={(e) => setMerchantKey(e.target.value)}
              required={!status?.configurado}
              autoComplete="new-password"
            />
            <Select
              label="Entorno"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value === "live" ? "live" : "test")}
              options={[
                { value: "test", label: "Test" },
                { value: "live", label: "Producción" },
              ]}
            />
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Estas credenciales permiten cobrar directamente a tus clientes. Asegúrate de introducirlas correctamente.
            </p>
            <div className="flex gap-2">
              {status?.configurado && (
                <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setSuccessMsg(null); }}>
                  Cancelar
                </Button>
              )}
              <Button type="submit" loading={saving}>
                Guardar configuración
              </Button>
            </div>
          </form>
        )}
      </section>

      {status?.configurado && (
        <RedsysRecoveryPanel endpoint="/cliente/configuracion/redsys/reprocesar" />
      )}

      <Modal
        isOpen={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        title="Generar API key"
        footer={null}
      >
        <div className="space-y-4">
          <Input
            label="Nombre"
            value={apiKeyNombre}
            onChange={(e) => setApiKeyNombre(e.target.value)}
            placeholder="Ej. Zapier producción"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setApiKeyModalOpen(false)}>
              Cancelar
            </Button>
            <Button loading={creatingApiKey} disabled={!apiKeyNombre.trim()} onClick={() => void handleCrearApiKey()}>
              Generar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={newApiKeySecret != null}
        onClose={() => {
          if (!apiKeyAcknowledged) return;
          setNewApiKeySecret(null);
          setNewApiKeyPreview(null);
          setApiKeyCopied(false);
          setApiKeyAcknowledged(false);
        }}
        title="API key generada"
        footer={null}
      >
        {newApiKeySecret && (
          <div className="space-y-4">
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Copia esta API key ahora. No volverás a verla.
            </p>
            {newApiKeyPreview && (
              <p className="text-xs text-gray-500">Preview: {newApiKeyPreview}</p>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                readOnly
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-800 bg-gray-50"
                value={newApiKeySecret}
              />
              <Button type="button" variant="secondary" className="shrink-0" onClick={() => void copyApiKey()}>
                {apiKeyCopied ? "✓ Copiado" : "Copiar"}
              </Button>
            </div>
            <label className="flex items-start gap-2 text-sm text-[#2C2C2C]">
              <input
                type="checkbox"
                className="mt-1"
                checked={apiKeyAcknowledged}
                onChange={(e) => setApiKeyAcknowledged(e.target.checked)}
              />
              <span>He copiado mi API key</span>
            </label>
            <div className="flex justify-end">
              <Button disabled={!apiKeyAcknowledged} onClick={() => {
                setNewApiKeySecret(null);
                setNewApiKeyPreview(null);
                setApiKeyCopied(false);
                setApiKeyAcknowledged(false);
              }}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={revocarApiKeyId != null}
        onClose={() => setRevocarApiKeyId(null)}
        onConfirm={() => handleRevocarApiKey()}
        title="Revocar API key"
        message="La integración que use esta key dejará de funcionar. ¿Continuar?"
        confirmLabel="Revocar"
        variant="danger"
      />
    </div>
  );
}
